/**
 * useArcTemplateEditor.ts
 *
 * Data hook for the Arc Editor Canvas (EAINT-951 Phase 0). Loads every version of one arc
 * template by `systemName` (`GET /arc-templates/?systemName=&includeDrafts=true` — the route
 * already decorates each row with the caller's own `canEdit`/`isSystem`, EAINT-951 D11) and
 * resolves the one the route names. `locked` is read straight off the row's server-computed
 * `canEdit` — never inferred from a 403 or an `organization_id` compare (frontend permissions
 * Rules 4-6) — and defaults to locked while pending or on any load error (fail closed).
 *
 * Catalogue resolution (schemas for lanes/date properties, aspects, approaches, intents,
 * contexts) that the full canvas editing surface needs is a later Phase 0 slice — this hook
 * carries only what the route-mount and the read-only gate need today.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ArcTemplateRow } from 'types/schema';
import { useApiClientsOrNull } from '../context/ApiClientsContext';

export type ArcTemplateEditorStatus = 'loading' | 'ready' | 'notFound' | 'degraded' | 'new';

export interface UseArcTemplateEditorResult {
  status: ArcTemplateEditorStatus;
  /** The resolved row for the route's `version` (or the highest version when `version` is
   * omitted), null unless `status === 'ready'`. Always null in `new` mode. */
  row: ArcTemplateRow | null;
  /** Fail-closed: `true` while `loading`/`degraded`/`notFound`, and whenever the resolved
   * row's own `canEdit` is not literally `true`. Always `false` in `new` mode — a template
   * that does not exist yet has no author to lock out. */
  locked: boolean;
  /** True when the resolved row's `organization_id` is the platform/system tenant. */
  isSystem: boolean;
  /** Re-runs the load after a `degraded` result (the load-failed state's Retry). */
  retry: () => void;
  /**
   * Swaps in the row the server returned from a save or publish (`PUT /arc-templates/…`), so
   * `row.definition`, `is_published` and `canEdit` reflect what is persisted without a refetch.
   * The canvas state hook treats the new `definition` reference as its saved baseline.
   */
  replaceRow: (row: ArcTemplateRow) => void;
}

/**
 * @param systemName - omit (or pass undefined) for the `/studio/arc/new` route; the hook
 *   resolves immediately to `status: 'new'` with no fetch.
 * @param version - the route's `:version` segment. When omitted for an existing systemName,
 *   resolves to the highest version fetched (mirrors `fetchPublishedArcTemplate`'s sort).
 */
export function useArcTemplateEditor(
  systemName: string | undefined,
  version: number | undefined
): UseArcTemplateEditorResult {
  const apiClients = useApiClientsOrNull();
  const schemaClient = apiClients?.schemaClient;

  const [rows, setRows] = useState<ArcTemplateRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  const replaceRow = useCallback((next: ArcTemplateRow) => {
    setRows((current) => {
      const others = (current ?? []).filter(
        (candidate) => !(candidate.system_name === next.system_name && candidate.version === next.version)
      );
      return [...others, next];
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!systemName || !schemaClient) {
      setRows(null);
      setLoading(false);
      setErrored(false);
      return;
    }

    setLoading(true);
    setErrored(false);

    schemaClient
      .get<ArcTemplateRow[]>(
        `/arc-templates/?systemName=${encodeURIComponent(systemName)}&includeDrafts=true`
      )
      .then((result) => {
        if (cancelled) return;
        setRows(Array.isArray(result) ? result : []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The page renders the localized error state with Retry; the cause goes to the
        // console so a failed load is diagnosable rather than a silent `errored`.
        console.error(`[useArcTemplateEditor] Failed to load arc template ${systemName}`, err);
        setRows(null);
        setErrored(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [systemName, schemaClient, attempt]);

  return useMemo(() => {
    if (!systemName) {
      return { status: 'new' as const, row: null, locked: false, isSystem: false, retry, replaceRow };
    }
    if (loading || !schemaClient) {
      return { status: 'loading' as const, row: null, locked: true, isSystem: false, retry, replaceRow };
    }
    if (errored) {
      return { status: 'degraded' as const, row: null, locked: true, isSystem: false, retry, replaceRow };
    }
    const resolved = version != null
      ? (rows ?? []).find((candidate) => candidate.version === version) ?? null
      : (rows ?? []).reduce<ArcTemplateRow | null>(
          (latest, candidate) => (!latest || candidate.version > latest.version ? candidate : latest),
          null
        );
    if (!resolved) {
      return { status: 'notFound' as const, row: null, locked: true, isSystem: false, retry, replaceRow };
    }
    return {
      status: 'ready' as const,
      row: resolved,
      locked: resolved.canEdit !== true,
      isSystem: resolved.isSystem === true,
      retry,
      replaceRow,
    };
  }, [systemName, version, loading, errored, rows, schemaClient, retry, replaceRow]);
}

export default useArcTemplateEditor;
